import moment from "moment";
import { useState, useCallback, useEffect } from "react";
import { Field, reduxForm } from "redux-form";
import { Button, Input, Label } from "expano-components";
import getRootTaxonOptions from "utils/select_options/getRootTaxonOptions";

import {
  Main,
  Dashboard,
  Select,
  DatePicker,
  PageLoader,
  AsyncSelect,
} from "components";

import useGet from "hooks/useGet";
import useCreate from "hooks/useCreate";

import channel_campaign_statuses_options from "utils/select_options/channel_campaign_statuses_options";
import channel_campaign_group_kind_options from "utils/select_options/channel_campaign_group_kind_options";

import { ReactComponent as ProductIcon } from "icons/product.svg";

import CampaignProducts from "./components/CampaignProducts";

const InputField = (props) => <Input {...props} />;
const SelectField = (props) => <Select {...props} />;
const MultiSelectField = (props) => <Select isMulti {...props} />;
const DatePickerField = (props) => <DatePicker {...props} />;

const placement_ids_options = [
  { label: "Wyniki wyszukiwania", value: "listing" },
  { label: "Lewa kolumna", value: "leftpanel" },
  { label: "Strona produktu", value: "showitem" },
  { label: "Pozostałe miejsca emisji", value: "mainpage" },
  { label: "Sieć reklamowa - Google Ads", value: "google" },
];

const convertValues = (values) => {
  const data = { ...values };
  if (data.display_start) {
    data.display_start = moment(values.display_start).format("DD-MM-YYYY");
  }

  if (data.display_end) {
    data.display_end = moment(values.display_end).format("DD-MM-YYYY");
  }

  return data;
};

const ChannelCampaignNew = ({
  handleSubmit,
  change,
  pristine,
  history,
  invalid,
  match: {
    params: { channel_id },
  },
}) => {
  const [root_taxon_id, setRootTaxonId] = useState(null);
  const [product_ids, setProductIds] = useState([]);

  const { data: channel, is_loading } = useGet({
    query: ["campaign_channel", channel_id],
    path: `/channels/${channel_id}`,
    context: "ui_settings",
    error_message_enabled: false,
  });

  const { is_creating, create } = useCreate({
    path: `/channels/${channel_id}/channel_campaigns`,
    onSuccess: ({ id }) => {
      history.push(`/campaigns/channels/${channel_id}/channel_campaigns/${id}`);
    },
  });

  useEffect(() => {
    change("display_start", new Date());
  }, []);

  const handleRemoveProduct = useCallback(
    (id) => {
      setProductIds(product_ids.filter((product_id) => product_id !== id));
    },
    [product_ids, setProductIds],
  );

  const handleAddProducts = useCallback(
    (ids) => {
      setProductIds([...new Set([...product_ids, ...ids])]);
    },
    [product_ids, setProductIds],
  );

  const handleClearProducts = useCallback(() => {
    setProductIds([]);
  }, [setProductIds]);

  const onSubmit = (values) =>
    create({ ...convertValues(values), root_taxon_id, product_ids });

  if (is_loading) {
    return <PageLoader />;
  }

  return (
    <Main
      page={{
        name: "Dodaj kampanię",
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Kampanine reklamowe",
            path: `/campaigns/channels/${channel_id}`,
          },
          { name: "Dodaj kampanię" },
        ],
      }}
    >
      <Dashboard
        header={{
          title: (
            <>
              <Label
                type="double"
                left={{
                  text: channel?.shop?.label_text,
                  color: `#${channel?.shop?.label_color}`,
                }}
                right={{
                  text: channel?.platform?.label_text,
                  color: `#${channel?.platform?.label_color}`,
                }}
              />
              <span>{channel?.identifier}</span>
            </>
          ),
          aside: (
            <div className="df" style={{ gap: "10px" }}>
              <Button
                type="cancel"
                onClick={() =>
                  history.push(`/campaigns/channels/${channel_id}`)
                }
              />
              <Button
                type="save"
                text={is_creating ? "Proszę czekać..." : "Utwórz"}
                disabled={
                  pristine || invalid || is_creating || !product_ids.length
                }
                onClick={handleSubmit(onSubmit)}
              />
            </div>
          ),
        }}
      />
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Dashboard header={{ title: "Kampania", icon: "info" }}>
              <div className="dashboard__form">
                <Field
                  name="campaign_name"
                  label="Nazwa"
                  component={InputField}
                  type="text"
                />
                <AsyncSelect
                  label="Kategoria"
                  getData={(q) => getRootTaxonOptions(q)}
                  onChange={(item) => {
                    handleClearProducts();
                    setRootTaxonId(item.value);
                    setProductIds(item.product_ids);
                    change("name", `${item.identifier} ${item.name}`);
                  }}
                />
              </div>
            </Dashboard>
            <Dashboard header={{ title: "Grupa kampanii", icon: "info" }}>
              <div className="dashboard__form">
                <Field
                  name="kind"
                  label="Rodzaj"
                  component={SelectField}
                  options={channel_campaign_group_kind_options}
                  handleChange={(value) => change("kind", value)}
                />
                <Field
                  name="name"
                  label="Nazwa"
                  component={InputField}
                  type="text"
                />
                <Field
                  name="status"
                  label="Status"
                  component={SelectField}
                  options={channel_campaign_statuses_options}
                  handleChange={(value) => change("status", value)}
                />
                <Field
                  name="sales_period_standard_cpc"
                  label="Standardowa stawka CPC"
                  component={InputField}
                  type="number"
                  placeholder=">0.65 <10"
                />
                <Field
                  name="budget_daily"
                  label="Budżet dzienny"
                  component={InputField}
                  type="number"
                />
                <Field
                  name="budget_total"
                  label="Budżet całkowity"
                  component={InputField}
                  type="number"
                />
                <Field
                  name="placement_ids"
                  label="Miejsce emisji"
                  component={MultiSelectField}
                  options={placement_ids_options}
                  handleChange={(values) => change("placement_ids", values)}
                />
                <div className="row m-t-20">
                  <div className="box-6">
                    <Field
                      min_date={new Date()}
                      name="display_start"
                      label="Data rozpoczęcia emisji"
                      component={DatePickerField}
                      onChange={(date) => change("display_start", date)}
                    />
                  </div>
                  <div className="box-6">
                    <Field
                      min_date={new Date()}
                      name="display_end"
                      label="Data zakończenia emisji"
                      component={DatePickerField}
                      onChange={(date) => change("display_end", date)}
                    />
                  </div>
                </div>
              </div>
            </Dashboard>
          </form>
        </div>
        <div className="column-half">
          <CampaignProducts
            channel_id={channel_id}
            product_ids={product_ids}
            handleRemoveProduct={handleRemoveProduct}
            handleAddProducts={handleAddProducts}
          />
        </div>
      </div>
    </Main>
  );
};

const validate = (values) => {
  const errors = {};

  if (!values.kind) {
    errors.kind = "Rodzaj kampanii jest wymagany!";
  }
  if (!values.name) {
    errors.name = "Nazwa grupy kampanii jest wymagana!";
  }
  if (!values.campaign_name) {
    errors.campaign_name = "Nazwa kampanii jest wymagana!";
  }
  if (!values.status) {
    errors.status = "Status kampanii jest wymagany!";
  }
  if (!values.display_start) {
    errors.display_start = "Start kampanii jest wymagany!";
  }
  if (!values.sales_period_standard_cpc) {
    errors.sales_period_standard_cpc = "Standardowa stawka CPC jest wymagana!";
  }
  if (!values.budget_daily) {
    errors.budget_daily = "Dzienny budżet jest wymagany!";
  }

  return errors;
};

export default reduxForm({
  form: "create-channel-campaign",
  enableReinitialize: true,
  validate,
})(ChannelCampaignNew);
